/**
 * Instanciation GPU avec niveaux de détail.
 *
 * Un `InstancedLodSet` = un InstancedMesh par niveau de détail d'un asset.
 * À chaque mise à jour, le CPU teste chaque instance (frustum, distance),
 * choisit son LOD et la recopie, compactée, dans le tampon de ce LOD :
 * le GPU ne reçoit que les instances visibles, en un draw call par LOD.
 */
import * as THREE from 'three/webgpu';

export class Frustum {
  private readonly planes = new Float32Array(24);
  private readonly f = new THREE.Frustum();
  private readonly m = new THREE.Matrix4();

  setFromCamera(camera: THREE.Camera): void {
    this.m.multiplyMatrices(camera.projectionMatrix, camera.matrixWorldInverse);
    this.f.setFromProjectionMatrix(this.m, camera.coordinateSystem);
    for (let i = 0; i < 6; i++) {
      const p = this.f.planes[i] as THREE.Plane;
      this.planes[i * 4] = p.normal.x;
      this.planes[i * 4 + 1] = p.normal.y;
      this.planes[i * 4 + 2] = p.normal.z;
      this.planes[i * 4 + 3] = p.constant;
    }
  }

  /** Vrai si la sphère (x, y, z, r) touche le frustum. */
  sphere(x: number, y: number, z: number, r: number): boolean {
    const p = this.planes;
    for (let i = 0; i < 24; i += 4) {
      if ((p[i] as number) * x + (p[i + 1] as number) * y + (p[i + 2] as number) * z + (p[i + 3] as number) < -r) return false;
    }
    return true;
  }

  box(minX: number, minY: number, minZ: number, maxX: number, maxY: number, maxZ: number): boolean {
    const p = this.planes;
    for (let i = 0; i < 24; i += 4) {
      const nx = p[i] as number;
      const ny = p[i + 1] as number;
      const nz = p[i + 2] as number;
      const x = nx >= 0 ? maxX : minX;
      const y = ny >= 0 ? maxY : minY;
      const z = nz >= 0 ? maxZ : minZ;
      if (nx * x + ny * y + nz * z + (p[i + 3] as number) < 0) return false;
    }
    return true;
  }
}

/**
 * Écrit dans `out` (offset o) la matrice T * Ry(yaw) * Rz(roll) * S, en
 * colonnes, sans allocation.
 */
export function composeYawRoll(out: Float32Array, o: number, x: number, y: number, z: number, yaw: number, roll: number, sx: number, sy: number, sz: number): void {
  const ct = Math.cos(yaw);
  const st = Math.sin(yaw);
  const cp = roll === 0 ? 1 : Math.cos(roll);
  const sp = roll === 0 ? 0 : Math.sin(roll);
  out[o] = ct * cp * sx;
  out[o + 1] = sp * sx;
  out[o + 2] = -st * cp * sx;
  out[o + 3] = 0;
  out[o + 4] = -ct * sp * sy;
  out[o + 5] = cp * sy;
  out[o + 6] = st * sp * sy;
  out[o + 7] = 0;
  out[o + 8] = st * sz;
  out[o + 9] = 0;
  out[o + 10] = ct * sz;
  out[o + 11] = 0;
  out[o + 12] = x;
  out[o + 13] = y;
  out[o + 14] = z;
  out[o + 15] = 1;
}

export interface LodSetOptions {
  /** Les LOD d'indice inférieur projettent des ombres. */
  shadowLods?: number;
  receiveShadow?: boolean;
  dynamic?: boolean;
}

export class InstancedLodSet {
  readonly meshes: THREE.InstancedMesh[] = [];
  private readonly tints: THREE.InstancedBufferAttribute[] = [];
  readonly counts: number[];
  private capacity: number;
  /** Multiplicateur global des distances de LOD (réglage qualité). */
  static lodScale = 1;
  readonly triangles: number[];

  constructor(
    readonly name: string,
    parent: THREE.Object3D,
    geometries: THREE.BufferGeometry[],
    material: THREE.Material,
    capacity: number,
    /** Distance maximale de chaque LOD ; au-delà du dernier : non affiché. */
    readonly distances: number[],
    opts: LodSetOptions = {},
  ) {
    this.capacity = Math.max(1, capacity);
    const levels = Math.min(geometries.length, distances.length);
    this.counts = new Array(levels).fill(0);
    this.triangles = [];
    for (let l = 0; l < levels; l++) {
      const src = geometries[l] as THREE.BufferGeometry;
      const geo = new THREE.BufferGeometry();
      for (const [k, attr] of Object.entries(src.attributes)) geo.setAttribute(k, attr);
      if (src.index) geo.setIndex(src.index);
      const tint = new THREE.InstancedBufferAttribute(new Float32Array(this.capacity * 3).fill(1), 3);
      tint.setUsage(THREE.DynamicDrawUsage);
      geo.setAttribute('tint', tint);
      geo.boundingSphere = new THREE.Sphere(new THREE.Vector3(), 1e6);
      const mesh = new THREE.InstancedMesh(geo, material, this.capacity);
      mesh.name = `${name}_LOD${l}`;
      mesh.count = 0;
      mesh.frustumCulled = false;
      mesh.instanceMatrix.setUsage(opts.dynamic ? THREE.DynamicDrawUsage : THREE.StaticDrawUsage);
      mesh.castShadow = l < (opts.shadowLods ?? 0);
      mesh.receiveShadow = opts.receiveShadow ?? true;
      parent.add(mesh);
      this.meshes.push(mesh);
      this.tints.push(tint);
      this.triangles.push((src.index ? src.index.count : src.attributes.position!.count) / 3);
    }
  }

  begin(): void {
    this.counts.fill(0);
  }

  /** LOD retenu pour une distance donnée (-1 = trop loin). */
  lodFor(distance: number): number {
    const s = InstancedLodSet.lodScale;
    for (let l = 0; l < this.counts.length; l++) if (distance <= (this.distances[l] as number) * s) return l;
    return -1;
  }

  /** Ajoute une instance (matrice 4x4 en colonnes dans m à partir de o). */
  add(m: Float32Array, o: number, distance: number, r = 1, g = 1, b = 1): boolean {
    const l = this.lodFor(distance);
    if (l < 0) return false;
    let n = this.counts[l] as number;
    if (n >= this.capacity) this.grow(this.capacity * 2);
    const mesh = this.meshes[l] as THREE.InstancedMesh;
    (mesh.instanceMatrix.array as Float32Array).set(m.subarray(o, o + 16), n * 16);
    const t = this.tints[l]!.array as Float32Array;
    t[n * 3] = r;
    t[n * 3 + 1] = g;
    t[n * 3 + 2] = b;
    this.counts[l] = n + 1;
    n++;
    return true;
  }

  end(): void {
    for (let l = 0; l < this.meshes.length; l++) {
      const mesh = this.meshes[l] as THREE.InstancedMesh;
      const n = this.counts[l] as number;
      mesh.count = n;
      mesh.visible = n > 0;
      if (n > 0) {
        const im = mesh.instanceMatrix;
        im.clearUpdateRanges();
        im.addUpdateRange(0, n * 16);
        im.needsUpdate = true;
        const t = this.tints[l] as THREE.InstancedBufferAttribute;
        t.clearUpdateRanges();
        t.addUpdateRange(0, n * 3);
        t.needsUpdate = true;
      }
    }
  }

  private grow(cap: number): void {
    this.capacity = cap;
    for (let l = 0; l < this.meshes.length; l++) {
      const mesh = this.meshes[l] as THREE.InstancedMesh;
      const old = mesh.instanceMatrix;
      const arr = new Float32Array(cap * 16);
      arr.set(old.array as Float32Array);
      const im = new THREE.InstancedBufferAttribute(arr, 16);
      im.setUsage(old.usage);
      mesh.instanceMatrix = im;
      const oldT = this.tints[l] as THREE.InstancedBufferAttribute;
      const tarr = new Float32Array(cap * 3).fill(1);
      tarr.set(oldT.array as Float32Array);
      const t = new THREE.InstancedBufferAttribute(tarr, 3);
      t.setUsage(THREE.DynamicDrawUsage);
      mesh.geometry.setAttribute('tint', t);
      this.tints[l] = t;
    }
  }

  get instanceCount(): number {
    let n = 0;
    for (const c of this.counts) n += c;
    return n;
  }

  get triangleCount(): number {
    let n = 0;
    for (let l = 0; l < this.counts.length; l++) n += (this.counts[l] as number) * (this.triangles[l] as number);
    return n;
  }

  dispose(): void {
    for (const m of this.meshes) {
      m.removeFromParent();
      m.geometry.dispose();
      m.dispose();
    }
  }
}

/** Couleur hexadécimale sRGB vers composantes linéaires. */
export function linear(hex: number): [number, number, number] {
  const c = new THREE.Color(hex);
  return [c.r, c.g, c.b];
}
